import * as bootstrapBundle from 'bootstrap';
import defaultJsKit from './jsKit';
import { initChoices } from './choices';

let activeJsKit = defaultJsKit;
let activeBootstrap = bootstrapBundle;
const getBootstrap = () => activeBootstrap;

const deliveryPluginContentSelector = '#delivery_plugin_content';
const paymentPluginContentSelector = '#payment_plugin_content';
let paymentCountdownInterval = null;

const setHtml = (selector, html) => {
  const element = document.querySelector(selector);
  if (element && typeof html !== 'undefined') {
    element.innerHTML = html;
  }

  return element;
};

const setVisible = (selector, visible = true) => {
  const element = document.querySelector(selector);
  if (element) {
    element.style.display = visible ? 'block' : 'none';
  }

  return element;
};

const updateBasketFragments = (data) => {
  if (!data) {
    return;
  }

  setHtml('#payment-options', data.payment_options);
  setHtml('#delivery-options', data.delivery_options);
  setHtml('#basket-teaser', data.basket_teaser);
  setHtml('#xs-basket-teaser', data.basket_teaser);
  setHtml('#basket-summary', data.basket_summary);
  setHtml('#basket-content', data.basket_content);
  setHtml('#vat-addition', data.vat_addition);
  activeJsKit.initTooltipsIn(document.getElementById('delivery-options'));
  activeJsKit.initTooltipsIn(document.getElementById('payment-options'));
};

const getInputValue = (selector) => {
  const input = document.querySelector(selector);
  return input ? input.value : '';
};

const getPluginConfig = (element, dataItem) => {
  if (!element) {
    return { plugin: '', orderHash: '' };
  }

  const [plugin, inlineOrderHash = ''] = (element.dataset[dataItem] || '').split(', ');
  return {
    plugin,
    orderHash: element.dataset.paymentOrderHash || inlineOrderHash,
  };
};

const basketPlugins = {
  configure: ({ jsKit = null, bootstrap = null } = {}) => {
    activeJsKit = jsKit || defaultJsKit;
    activeBootstrap = bootstrap;
  },

  initJsChoice: () => initChoices(),

  showDeliveryPluginOptions: async (plugin, isShown = false) => {
    if (!plugin) {
      return;
    }

    const target = document.querySelector(deliveryPluginContentSelector);
    if (!target) {
      return;
    }

    const data = await activeJsKit.requestUrl(`/plugins/${plugin}/showOptions`);
    if (data && data.html) {
      target.innerHTML = data.html;
      setVisible(deliveryPluginContentSelector, true);

      if (isShown || target.classList.contains('collapse')) {
        target.classList.add('show');
      }

      basketPlugins.initJsChoice();
    }
  },

  showDeliveryOptionPanel: async (e = null, isShown = false) => {
    if (e) {
      e.preventDefault();
    }

    const target = e ? e.delegateTarget : null;
    activeJsKit.setLoading(target, true);

    const pluginContent = document.querySelector(deliveryPluginContentSelector);
    const { plugin } = getPluginConfig(pluginContent, 'jsShowDeliveryPluginOptions');

    await basketPlugins.showDeliveryPluginOptions(plugin, isShown);
    activeJsKit.setLoading(target, false);
  },

  setDeliveryOption: async (e) => {
    e.preventDefault();

    const target = e.delegateTarget;
    const optionId = target.dataset.jsSetDeliveryOption;
    if (!optionId) {
      return;
    }

    const formData = new FormData();
    formData.append('basket_delivery_option_form[delivery_option]', optionId);

    activeJsKit.setLoading(target, true);
    const data = await activeJsKit.requestUrl('/basket/setDeliveryOption/', 'json', 'POST', formData);
    activeJsKit.setLoading(target, false);

    if (data && data.validated) {
      updateBasketFragments(data);
      await basketPlugins.showDeliveryOptionPanel(null, true);
    } else {
      activeJsKit.showErrors(data, '#basket_delivery_payment_form');
    }
  },

  setPaymentOption: async (e) => {
    e.preventDefault();

    const target = e.delegateTarget;
    const optionId = target.dataset.jsSetPaymentOption;
    if (!optionId) {
      return;
    }

    const formData = new FormData();
    formData.append('basket_payment_option_form[payment_option]', optionId);

    activeJsKit.setLoading(target, true);
    const data = await activeJsKit.requestUrl('/basket/setPaymentOption/', 'json', 'POST', formData);
    activeJsKit.setLoading(target, false);

    if (data && data.validated) {
      updateBasketFragments(data);
      await basketPlugins.showPaymentOptionPanel();
    } else {
      activeJsKit.showErrors(data, '#basket_delivery_payment_form');
    }
  },

  changeDeliveryCountry: async (e) => {
    e.preventDefault();

    const target = e.delegateTarget || e.currentTarget;
    const countryId = target?.value;
    if (!countryId) {
      return;
    }

    const data = await activeJsKit.requestUrl(`/basket/changeDeliveryCountry/${countryId}`);
    if (data && data.validated) {
      updateBasketFragments(data);

      if (data.delivery_customs_message) {
        setHtml('#delivery-customs-message', data.delivery_customs_message);
        setVisible('#delivery-customs-message', true);
      } else {
        setVisible('#delivery-customs-message', false);
        setHtml('#delivery-customs-message', '');
      }
    }
  },

  reloadDeliveryOptions: async () => {
    const data = await activeJsKit.requestUrl('/basket/reloadDeliveryOptions/');
    if (data && data.validated) {
      updateBasketFragments(data);
      await basketPlugins.showDeliveryOptionPanel(null, true);
    }
  },

  reloadPaymentOptions: async () => {
    const data = await activeJsKit.requestUrl('/basket/reloadPaymentOptions/');
    if (data && data.validated) {
      updateBasketFragments(data);
    }
  },

  findInpostMachines: async (e) => {
    e.preventDefault();

    const target = e.delegateTarget;
    const formData = new FormData();
    formData.append('inpost_search[zipcode]', getInputValue('[name="inpost_search[zipcode]"]'));
    formData.append('inpost_search[city]', getInputValue('[name="inpost_search[city]"]'));

    activeJsKit.setLoading(target, true);
    const data = await activeJsKit.requestUrl('/plugins/inpost/findMachines/', 'json', 'POST', formData);
    activeJsKit.setLoading(target, false);

    if (data && data.validated) {
      setHtml('#inpost_machines_dropdown', data.html);
      basketPlugins.initJsChoice();
    } else {
      activeJsKit.showErrors(data, '#inpost_search');
    }
  },

  selectInPostOption: async (e) => {
    e.preventDefault();

    const target = e.delegateTarget;
    const inpostMachineId = getInputValue('#inpost_machine_id');
    if (!inpostMachineId) {
      return;
    }

    activeJsKit.setLoading(target, true);
    const data = await activeJsKit.requestUrl(`/plugins/inpost/selectOption/${inpostMachineId}`);
    activeJsKit.setLoading(target, false);

    if (data && data.validated) {
      activeJsKit.showNotificationsPanel('Paczkomat został wybrany', 'success');
      const inPostModal = document.querySelector('#inPostModal');
      const bootstrap = getBootstrap();

      if (inPostModal && inPostModal.classList.contains('show') && bootstrap) {
        const modal = bootstrap.Modal.getInstance(inPostModal);
        if (modal) {
          inPostModal.addEventListener('hidden.bs.modal', () => basketPlugins.reloadDeliveryOptions(), { once: true });
          modal.hide();
          return;
        }
      }

      await basketPlugins.reloadDeliveryOptions();
    } else {
      activeJsKit.showNotificationsPanel('Paczkomat nie został wybrany');
    }
  },

  showPaymentPluginOptions: async (plugin, orderHash = '') => {
    if (!plugin) {
      return;
    }

    const target = document.querySelector(paymentPluginContentSelector);
    if (!target) {
      return;
    }

    const data = await activeJsKit.requestUrl(`/plugins/${plugin}/showOptions/${orderHash}`);
    if (data && data.html) {
      target.innerHTML = data.html;
      basketPlugins.finalizePaymentCountdown();
      if (data.direct_pay == 1) {
        if (plugin === 'paypal') {
          await basketPlugins.createPayment(orderHash);
        } else if (plugin === 'payu' || plugin === 'payucard') {
          const form = document.querySelector('#payu_form');
          if (form) {
            form.requestSubmit();
          }
        }
      }
    } else {
      target.innerHTML = '';
      target.classList.remove('border');
    }
  },

  showPaymentOptionPanel: async () => {
    const target = document.querySelector(paymentPluginContentSelector);
    if (!target) {
      return;
    }

    const { plugin, orderHash } = getPluginConfig(target, 'jsShowPaymentPluginOptions');
    await basketPlugins.showPaymentPluginOptions(plugin, orderHash);
  },

  createPayment: async (orderHash) => {
    if (!orderHash) {
      activeJsKit.showNotificationsPanel('Podaj poprawny identyfikator zamówienia!');
      return;
    }

    const data = await activeJsKit.requestUrl(`/plugins/paypal/createPayment/${orderHash}`);
    if (data && data.redirect) {
      window.location = data.redirect;
    } else {
      activeJsKit.showNotificationsPanel(data && data.error_message ? data.error_message : 'Brak poprawnej odpowiedzi ...');
    }
  },

  finalizePayment: () => {
    if (paymentCountdownInterval) {
      clearInterval(paymentCountdownInterval);
      paymentCountdownInterval = null;
    }

    window.location = '/plugins/paypal/finalizePayment/';
  },

  finalizePaymentCountdown: () => {
    const counterElement = document.getElementById('finalizePaymentCounter');
    if (!counterElement) {
      return;
    }

    let counter = 3;
    paymentCountdownInterval = setInterval(() => {
      counter -= 1;
      counterElement.textContent = counter;

      if (counter === 0) {
        basketPlugins.finalizePayment();
      }
    }, 1000);
  },
};

export const basketPluginEvents = [
  ['jsShowDeliveryPluginOptions', () => basketPlugins.showDeliveryOptionPanel(null, true), 'DOMContentLoaded'],
  ['jsShowDeliveryOptionPanel', (e) => basketPlugins.showDeliveryOptionPanel(e)],
  ['jsSetDeliveryOption', (e) => basketPlugins.setDeliveryOption(e)],
  ['jsChangeDeliveryCountry', (e) => basketPlugins.changeDeliveryCountry(e), 'change'],
  ['jsShowPaymentOptionPanel', () => basketPlugins.showPaymentOptionPanel(), 'click'],
  ['jsSetPaymentOption', (e) => basketPlugins.setPaymentOption(e), 'change'],
  ['jsFindInpostMachines', (e) => basketPlugins.findInpostMachines(e)],
  ['jsSelectInPostOption', (e) => basketPlugins.selectInPostOption(e)],
  ['jsShowPaymentPluginOptions', () => basketPlugins.showPaymentOptionPanel(), 'DOMContentLoaded'],
  ['jsCreatePayment', (e) => {
    e.preventDefault();
    basketPlugins.createPayment(e.delegateTarget.dataset.jsCreatePayment);
  }],
  ['jsFinalizePayment', (e) => {
    e.preventDefault();
    basketPlugins.finalizePayment();
  }],
  ['jsFinalizePaymentCountdown', () => basketPlugins.finalizePaymentCountdown(), 'DOMContentLoaded'],
];

export default basketPlugins;
